/* eslint-disable no-underscore-dangle */
import { FacetMetadata } from '@algolia/facets-metadata';

const parseThousandsFacetValue = (value) => {
  const numValue = Number(String(value).replace(/\./g, ''));
  return Number.isNaN(numValue) ? null : numValue;
};

const parseDecimalFacetValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numValue = Number(String(value).trim().replace(',', '.'));
  return Number.isNaN(numValue) ? null : numValue;
};

const formatBreakpointRangeLabel = (min, max) => {
  const isTenWideInteger = Number.isInteger(min) && Number.isInteger(max) && max - min === 10;

  if (isTenWideInteger) {
    return `${min} - ${max - 1}`;
  }

  const formatNumber = (n) => String(n).replace('.', ',');
  return `${formatNumber(min)} - ${formatNumber(max)}`;
};

const extendBreakpointsToCover = (breakpoints, maxValue) => {
  const points = [...breakpoints];
  const extensionStep = points[points.length - 1] - points[points.length - 2];
  let lastPoint = points[points.length - 1];

  while (extensionStep > 0 && lastPoint <= maxValue) {
    lastPoint += extensionStep;
    points.push(lastPoint);
  }

  return points;
};

const getGroupedNumericMetadata = (attribute) => {
  const metadata = FacetMetadata?.FACET_METADATA?.[attribute];
  return metadata?.type === 'grouped_numeric' ? metadata : null;
};

const getGroupedNumericRangeLabel = (value, breakpoints) => {
  if (Array.isArray(breakpoints) && breakpoints.length >= 2) {
    const numValue = parseDecimalFacetValue(value);
    if (numValue === null) return null;

    const points = extendBreakpointsToCover(breakpoints, numValue);
    for (let i = 0; i < points.length - 1; i += 1) {
      if (numValue >= points[i] && numValue < points[i + 1]) {
        return formatBreakpointRangeLabel(points[i], points[i + 1]);
      }
    }

    return null;
  }

  const numValue = parseThousandsFacetValue(value);
  if (numValue === null) return null;

  const rangeMin = Math.floor(numValue / 100) * 100;
  return `${rangeMin}-${rangeMin + 99}`;
};

export class AlgoliaUiComponents {
  static isHierarchicalMenuFirstElementClicked = false;
  static hasLoggedInitialPaths = false;
  static renderRangeSliderCacheByContainer = new WeakMap();
  static renderSortByCache = { _refs: {} };

  /**
   * Creates a DOM element with optional class names and attributes.
   * @param {string} tag
   * @param {string} [classNames='']
   * @param {Object} [attrs={}]
   * @returns {HTMLElement}
   */
  static createElem(tag, classNames = '', attrs = {}) {
    const el = document.createElement(tag);
    if (classNames) el.className = classNames;

    Object.entries(attrs).forEach(([key, value]) => {
      el.setAttribute(key, value);
    });

    return el;
  }

  // Helper function to scroll to the current refinements when a facet is used

  // Initialize filter button functionality for search and collection pages
  static initializeFilterButtons() {
    const attachFilterButtonListener = (button) => {
      if (button.hasAttribute('data-filter-listener-attached')) return;

      button.setAttribute('data-filter-listener-attached', 'true');
      button.addEventListener('click', () => {
        const drawerFilter = document.querySelector(
          '.collection-products-section__filter-drawer-container'
        );

        if (drawerFilter) {
          drawerFilter.classList.add('collection-products-section__filter-drawer-container--open');
          document.body.style.overflow = 'hidden';
        }
      });
    };

    const filterButtons = document.querySelectorAll('.filter-sorting-btn');
    filterButtons.forEach(attachFilterButtonListener);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList && node.classList.contains('filter-sorting-btn')) {
              attachFilterButtonListener(node);
            }

            const descendantButtons = node.querySelectorAll
              ? node.querySelectorAll('.filter-sorting-btn')
              : [];

            descendantButtons.forEach(attachFilterButtonListener);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const closeDrawer = () => {
      const drawerFilter = document.querySelector(
        '.collection-products-section__filter-drawer-container'
      );

      if (drawerFilter) {
        drawerFilter.classList.remove('collection-products-section__filter-drawer-container--open');
        document.body.style.overflow = '';
      }
    };

    const attachCloseHandlers = () => {
      const closeButton = document.querySelector('.filter-drawer-header__close');
      const overlay = document.querySelector('.collection-products-section__filter-drawer-overlay');
      const footerButton = document.querySelector('.filter-drawer__footer-btn');

      if (closeButton && !closeButton.hasAttribute('data-close-listener-attached')) {
        closeButton.setAttribute('data-close-listener-attached', 'true');
        closeButton.addEventListener('click', closeDrawer);
      }

      if (overlay && !overlay.hasAttribute('data-overlay-listener-attached')) {
        overlay.setAttribute('data-overlay-listener-attached', 'true');
        overlay.addEventListener('click', closeDrawer);
      }

      if (footerButton && !footerButton.hasAttribute('data-close-listener-attached')) {
        footerButton.setAttribute('data-close-listener-attached', 'true');
        footerButton.addEventListener('click', closeDrawer);
      }
    };

    attachCloseHandlers();

    const closeObserver = new MutationObserver(() => {
      attachCloseHandlers();
    });

    closeObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // --- renderRangeSlider ---
  static renderRangeSlider(renderOptions, isFirstRender) {
    const closeDropdown = (dropdownItem, body, icon) => {
      body.style.display = 'none';
      dropdownItem.classList.add('filter-dropdown-item--closed');
      dropdownItem.classList.remove('filter-dropdown-item--open');

      if (icon) {
        icon.style.transform = 'rotate(-180deg)';
        icon.style.transition = 'transform 0.3s ease';
      }
    };

    const openDropdown = (dropdownItem, body, icon) => {
      body.style.display = '';
      dropdownItem.classList.remove('filter-dropdown-item--closed');
      dropdownItem.classList.add('filter-dropdown-item--open');

      if (icon) {
        icon.style.transform = 'rotate(0deg)';
        icon.style.transition = 'transform 0.3s ease';
      }
    };

    const { range, start, refine, widgetParams } = renderOptions;
    const { container } = widgetParams;

    const containerElem =
      typeof container === 'string' ? document.querySelector(container) : container;

    if (!containerElem) return;

    let cache = AlgoliaUiComponents.renderRangeSliderCacheByContainer.get(containerElem);

    if (!cache) {
      cache = { refs: {} };
      AlgoliaUiComponents.renderRangeSliderCacheByContainer.set(containerElem, cache);
    }

    const rangeMin = Number.isFinite(range.min) ? range.min : 0;
    const rangeMax = Number.isFinite(range.max) ? range.max : 100;

    const refinedMin = Number.isFinite(start?.[0]) ? start[0] : rangeMin;
    const refinedMax = Number.isFinite(start?.[1]) ? start[1] : rangeMax;

    if (isFirstRender) {
      const wrapper = AlgoliaUiComponents.createElem('div', 'filter-dropdown-item');
      const header = AlgoliaUiComponents.createElem('div', 'filter-dropdown-item__header');

      const title = AlgoliaUiComponents.createElem(
        'span',
        'highlighted-text filter-dropdown-item__title h5 h5',
        { role: 'button' }
      );

      const titleText = AlgoliaUiComponents.createElem(
        'span',
        'text text--sm text--regular text--line-height-lg text--headline-font text--none-decoration'
      );

      titleText.textContent = window.searchTranslations?.filters?.price || 'Preis';
      title.appendChild(titleText);

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
      <svg
        width="16"
        height="16"
        aria-hidden="false"
        aria-label="Dropdown arrow"
        class="icon filter-dropdown-item__icon"
        style="--icon-fill: none; --icon-stroke: var(--color-foreground);"
      >
        <use xlink:href="#icon-arrow-filter"></use>
      </svg>
    `.trim();

      const icon = tempDiv.firstChild;

      header.append(title, icon);

      const body = AlgoliaUiComponents.createElem(
        'div',
        'filter-price filter-dropdown-item__body',
        {
          role: 'group',
          'aria-label': 'Price filter',
        }
      );

      const rangeComponent = AlgoliaUiComponents.createElem('filter-range', '', {
        id: `${containerElem.id}-range-component`,
      });

      rangeComponent.onCommit = ({ min, max }) => {
        refine([min, max]);
      };

      body.appendChild(rangeComponent);
      wrapper.append(header, body);
      containerElem.appendChild(wrapper);

      cache.refs = {
        wrapper,
        header,
        body,
        icon,
        rangeComponent,
      };

      closeDropdown(wrapper, body, icon);
      header.style.cursor = 'pointer';

      header.addEventListener('click', () => {
        const isOpen = body.style.display !== 'none';

        if (isOpen) {
          closeDropdown(wrapper, body, icon);
        } else {
          openDropdown(wrapper, body, icon);
        }
      });
    }

    const currency = window.Shopify?.currency?.active === 'CHF' ? 'CHF' : '€';

    cache.refs.rangeComponent.setState({
      min: rangeMin,
      max: rangeMax,
      selectedMin: refinedMin,
      selectedMax: refinedMax,
      currency,
    });

    // The price panel has no checkboxes, so the generic auto opening for refined
    // facets cannot detect it, the active range refinement is checked here instead.
    const isPriceRefined = Number.isFinite(start?.[0]) || Number.isFinite(start?.[1]);
    // The marker lets the generic auto opening restore this panel as well when
    // the drawer resets its panels on reopening.
    if (isPriceRefined) {
      cache.refs.rangeComponent.dataset.refined = 'true';
    } else {
      delete cache.refs.rangeComponent.dataset.refined;
    }
    if (
      isPriceRefined &&
      cache.refs.wrapper.dataset.userToggled !== 'true' &&
      cache.refs.body.style.display === 'none'
    ) {
      openDropdown(cache.refs.wrapper, cache.refs.body, cache.refs.icon);
    }
  }

  // --- renderSortBy ---
  static renderSortBy(renderOptions, isFirstRender) {
    const createIcon = (iconId, { width = 16, height = 16 } = {}) => {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', width);
      svg.setAttribute('height', height);
      svg.setAttribute('aria-hidden', 'true');
      svg.classList.add('icon');
      svg.style.setProperty('--icon-fill', 'none');
      svg.style.setProperty('--icon-stroke', 'var(--color-foreground)');

      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttribute('href', `#${iconId}`);
      svg.appendChild(use);

      return svg;
    };

    const { options, refine, currentRefinement, widgetParams } = renderOptions;
    const { container } = widgetParams;

    if (!container) return;

    const containerElem =
      typeof container === 'string' ? document.querySelector(container) : container;

    if (!containerElem) return;

    const isMobileDrawer = container === '#sorting-mobile';

    if (isMobileDrawer) {
      if (isFirstRender) {
        const wrapper = AlgoliaUiComponents.createElem(
          'filter-dropdown-item',
          'filter-dropdown-item filter-dropdown-item__sorting',
          { id: 'SortingDropdown-mobile' }
        );

        const header = AlgoliaUiComponents.createElem('div', 'filter-dropdown-item__header');
        const title = AlgoliaUiComponents.createElem(
          'span',
          'highlighted-text filter-dropdown-item__title h5',
          {
            role: 'button',
          }
        );

        const labelSpan = AlgoliaUiComponents.createElem(
          'span',
          'text text--sm text--regular text--line-height-lg text--headline-font text--none-decoration'
        );
        labelSpan.textContent = window.searchTranslations?.sort?.label || 'Sortieren nach: ';

        const valueSpan = AlgoliaUiComponents.createElem(
          'span',
          'text text--sm text--bold text--line-height-lg text--headline-font text--none-decoration text--highlighted js-filter-drawer-sort-header',
          { 'aria-marked': 'true', role: 'mark' }
        );
        valueSpan.textContent = window.searchTranslations?.sort?.recommended || 'Empfohlen';

        title.append(labelSpan, valueSpan);

        const arrowIcon = createIcon('icon-arrow-filter');
        arrowIcon.classList.add('filter-dropdown-item__icon');
        header.append(title, arrowIcon);

        const fieldset = AlgoliaUiComponents.createElem(
          'fieldset',
          'filter-checkboxes filter-dropdown-item__body',
          {
            'aria-label': 'Filter options',
            role: 'group',
          }
        );
        fieldset.style.display = 'none';

        wrapper.append(header, fieldset);
        containerElem.appendChild(wrapper);

        AlgoliaUiComponents.renderSortByCache._refs.mobileValueSpan = valueSpan;
        AlgoliaUiComponents.renderSortByCache._refs.mobileFieldset = fieldset;
        AlgoliaUiComponents.renderSortByCache._refs.mobileHeader = header;

        header.addEventListener('click', () => {
          const isOpen = fieldset.style.display !== 'none';
          fieldset.style.display = isOpen ? 'none' : 'flex';
          wrapper.classList.toggle('filter-dropdown-item--open', !isOpen);
        });
      }

      const { mobileValueSpan, mobileFieldset } = AlgoliaUiComponents.renderSortByCache._refs;
      const currentItem = options.find((item) => item.value === currentRefinement);

      if (currentItem && mobileValueSpan) {
        mobileValueSpan.textContent = currentItem.label;
      }

      if (mobileFieldset) {
        mobileFieldset.innerHTML = '';

        options.forEach((item) => {
          const label = AlgoliaUiComponents.createElem(
            'label',
            'field field--row filter-checkboxes__input'
          );
          const input = AlgoliaUiComponents.createElem('input', 'input js-filter-radio', {
            type: 'radio',
            name: 'sort_by_mobile',
            'data-filter-to-add': item.value,
          });

          if (item.value === currentRefinement) {
            input.checked = true;
          }

          const span = AlgoliaUiComponents.createElem('span', 'filter-field__label');
          span.textContent = item.label;

          label.append(input, span);
          mobileFieldset.appendChild(label);

          input.addEventListener('change', () => {
            if (input.checked) {
              refine(item.value);
              mobileFieldset.style.display = 'none';
              AlgoliaUiComponents.renderSortByCache._refs.mobileHeader.parentElement.classList.remove(
                'filter-dropdown-item--open'
              );
            }
          });
        });
      }
    } else {
      if (isFirstRender) {
        const wrapper = AlgoliaUiComponents.createElem(
          'div',
          'sorting-selector__container js-sorting-selector'
        );
        const header = AlgoliaUiComponents.createElem(
          'button',
          'sorting-selector__header button button-unstyled',
          {
            'aria-label': 'Select sorting option',
            'aria-controls': 'sorting-options',
            'aria-expanded': 'false',
          }
        );

        const valueDiv = AlgoliaUiComponents.createElem('div', 'sorting-selector__value');
        const span = AlgoliaUiComponents.createElem(
          'span',
          'text text--x-sm text--regular text--body-font text--none-decoration'
        );
        span.textContent = window.searchTranslations?.sort?.recommended || 'Empfohlen';
        valueDiv.appendChild(span);

        const arrowIcon = createIcon('icon-arrow-point-down');
        header.append(valueDiv, arrowIcon);

        const optionsList = AlgoliaUiComponents.createElem(
          'ul',
          'sorting-selector__options hidden',
          {
            id: 'sorting-options',
            role: 'listbox',
          }
        );

        wrapper.append(header, optionsList);
        containerElem.appendChild(wrapper);

        AlgoliaUiComponents.renderSortByCache._refs = { header, valueSpan: span, optionsList };

        header.addEventListener('click', () => {
          optionsList.classList.toggle('hidden');
          header.setAttribute(
            'aria-expanded',
            optionsList.classList.contains('hidden') ? 'false' : 'true'
          );
        });

        document.addEventListener('click', (event) => {
          if (!wrapper.contains(event.target)) {
            optionsList.classList.add('hidden');
            header.setAttribute('aria-expanded', 'false');
          }
        });
      }

      const { header, valueSpan, optionsList } = AlgoliaUiComponents.renderSortByCache._refs;
      const currentItem = options.find((item) => item.value === currentRefinement);

      if (currentItem) {
        valueSpan.textContent = currentItem.label;
      }

      optionsList.innerHTML = '';

      options.forEach((item) => {
        const isSelected = item.value === currentRefinement;
        const li = AlgoliaUiComponents.createElem(
          'li',
          `sorting-selector__option${isSelected ? ' sorting-selector__option--selected' : ''}`,
          { role: 'option', 'aria-selected': String(isSelected) }
        );

        const valDiv = AlgoliaUiComponents.createElem('div', 'sorting-selector__value');
        const span = AlgoliaUiComponents.createElem(
          'span',
          'text text--x-sm text--regular text--body-font text--none-decoration'
        );
        span.textContent = item.label;
        valDiv.appendChild(span);
        li.append(valDiv);

        if (isSelected) {
          const checkIcon = createIcon('icon-checkmark', { width: 16, height: 16 });
          checkIcon.classList.add('sorting-selector__icon');
          li.append(checkIcon);
        }

        li.addEventListener('click', () => {
          refine(item.value);
          optionsList.classList.add('hidden');
          header.setAttribute('aria-expanded', 'false');
        });

        optionsList.appendChild(li);
      });
    }
  }

  // --- createRefinementListWithHeader ---
  static createRefinementListWithHeader({ attribute, container, label }) {
    const { connectRefinementList } = window.instantsearch.connectors;
    const searchState = {};

    const renderRefinementList = (renderOptions, isFirstRender) => {
      const { items, refine, searchForItems, isFromSearch, isShowingMore, toggleShowMore } =
        renderOptions;

      const containerElem =
        typeof container === 'string' ? document.querySelector(container) : container;

      if (!containerElem) return;

      if (isFirstRender) {
        containerElem.innerHTML = `
          <div class="filter-dropdown-item">
            <div class="filter-dropdown-item__header">
              <span class="highlighted-text filter-dropdown-item__title h5" role="button">
                <span class="text text--sm text--regular text--line-height-lg text--headline-font text--none-decoration">
                  ${label}
                </span>
              </span>
              <svg width="16" height="16" aria-hidden="false" aria-label="Dropdown arrow" class="icon filter-dropdown-item__icon" style="--icon-fill: none; --icon-stroke: var(--color-foreground);">
                <use xlink:href="#icon-arrow-filter"></use>
              </svg>
            </div>
            <div class="filter-checkboxes filter-dropdown-item__body">
              ${
                items.length > 15
                  ? `
                <div class="filter-checkboxes__search">
                  <div class="filter-checkboxes__search-wrapper">
                    <input type="search" class="input filter-checkboxes__search-input js-filter-search" id="filter-search-${attribute}" placeholder="${window.searchTranslations?.filters?.searchPlaceholder || 'Suchen...'}" />
                    <button type="submit" class="filter-checkboxes__search-icon js-search-icon">
                      <svg class="icon icon-search" width="16" height="16">
                        <use class="js-search-icon-search" xlink:href="#icon-search"></use>
                      </svg>
                    </button>
                  </div>
                </div>
              `
                  : ''
              }
              <div class="filter-checkboxes__list"></div>
              ${
                items.length > 5
                  ? `
                <button type="button" class="button button-unstyled button--text filter-checkboxes__toggle-btn js-filter-checkboxes__toggle-btn">
                  <span class="button__text js-filter-checkboxes__toggle-text">${window.searchTranslations?.filters?.showAll || 'Alle anzeigen'}</span>
                </button>
              `
                  : ''
              }
            </div>
          </div>
        `;

        const header = containerElem.querySelector('.filter-dropdown-item__header');
        const body = containerElem.querySelector('.filter-dropdown-item__body');
        const dropdownItem = containerElem.querySelector('.filter-dropdown-item');
        const icon = containerElem.querySelector('.filter-dropdown-item__icon');

        if (header && body && dropdownItem) {
          body.style.display = 'none';
          dropdownItem.classList.add('filter-dropdown-item--closed');

          if (icon) {
            icon.style.transform = 'rotate(-180deg)';
          }

          header.addEventListener('click', () => {
            const isOpen = body.style.display !== 'none';

            if (isOpen) {
              body.style.display = 'none';
              dropdownItem.classList.add('filter-dropdown-item--closed');
              dropdownItem.classList.remove('filter-dropdown-item--open');

              if (icon) {
                icon.style.transform = 'rotate(-180deg)';
                icon.style.transition = 'transform 0.3s ease';
              }
            } else {
              body.style.display = '';
              dropdownItem.classList.remove('filter-dropdown-item--closed');
              dropdownItem.classList.add('filter-dropdown-item--open');

              if (icon) {
                icon.style.transform = 'rotate(0deg)';
                icon.style.transition = 'transform 0.3s ease';
              }
            }
          });

          header.style.cursor = 'pointer';
        }

        const searchInput = containerElem.querySelector('.js-filter-search');
        if (searchInput) {
          searchInput.addEventListener('input', (e) => {
            const query = e.target.value;
            searchState[attribute] = query;
            searchForItems(query);
          });
        }
      }

      const listContainer = containerElem.querySelector('.filter-checkboxes__list');
      const searchInput = containerElem.querySelector('.js-filter-search');
      const showMoreButton = containerElem.querySelector('.js-filter-checkboxes__toggle-btn');
      const dropdownItem = containerElem.querySelector('.filter-dropdown-item');

      if (listContainer) {
        const limit = isShowingMore ? 50 : 5;
        const visibleItems = items.slice(0, limit);

        dropdownItem.classList.toggle(
          'filter-dropdown-item--empty',
          visibleItems.length === 0 && !searchState[attribute]
        );

        listContainer.innerHTML =
          visibleItems.length > 0
            ? visibleItems
                .map(
                  (item) => `
            <label class="field field--row filter-checkboxes__input">
              <input class="input ${item.isRefined ? 'input--checked' : ''}" type="checkbox" value="${item.value}" ${item.isRefined ? 'checked' : ''} />
              <span class="filter-field__label">${item.label}</span>
              <span class="filter-checkboxes__count-option">${item.count}</span>
            </label>
          `
                )
                .join('')
            : '';

        listContainer.querySelectorAll('input[type="checkbox"]').forEach((checkbox, index) => {
          checkbox.addEventListener('change', () => {
            refine(visibleItems[index].value);
          });
        });
      }

      if (searchInput && !isFromSearch && searchState[attribute]) {
        searchInput.value = searchState[attribute];
        searchForItems(searchState[attribute]);
      }

      if (showMoreButton) {
        const showMoreText = showMoreButton.querySelector('.js-filter-checkboxes__toggle-text');

        if (showMoreText) {
          showMoreText.textContent = isShowingMore
            ? window.searchTranslations?.filters?.showLess || 'Weniger anzeigen'
            : window.searchTranslations?.filters?.showAll || 'Alle anzeigen';
        }

        showMoreButton.style.display = items.length > 5 ? '' : 'none';

        const newButton = showMoreButton.cloneNode(true);
        showMoreButton.parentNode.replaceChild(newButton, showMoreButton);
        newButton.addEventListener('click', (e) => {
          e.stopPropagation();
          toggleShowMore();
        });
      }

      const searchContainer = containerElem.querySelector('.filter-checkboxes__search');

      if ((items.length > 15 || searchState[attribute]) && !searchContainer) {
        const bodyElem = containerElem.querySelector('.filter-checkboxes');

        if (bodyElem) {
          const searchHTML = `
            <div class="filter-checkboxes__search">
              <div class="filter-checkboxes__search-wrapper">
                <input type="search" class="input filter-checkboxes__search-input js-filter-search" id="filter-search-${attribute}" placeholder="${window.searchTranslations?.filters?.searchPlaceholder || 'Suchen...'}" />
                <button type="submit" class="filter-checkboxes__search-icon js-search-icon">
                  <svg class="icon icon-search" width="16" height="16">
                    <use class="js-search-icon-search" xlink:href="#icon-search"></use>
                  </svg>
                </button>
              </div>
            </div>
          `;
          bodyElem.insertAdjacentHTML('afterbegin', searchHTML);

          const newSearchInput = bodyElem.querySelector('.js-filter-search');
          if (newSearchInput) {
            newSearchInput.addEventListener('input', (e) => {
              const query = e.target.value;
              searchState[attribute] = query;
              searchForItems(query);
            });

            if (searchState[attribute]) {
              newSearchInput.value = searchState[attribute];
              searchForItems(searchState[attribute]);
            }
          }
        }
      } else if (items.length <= 15 && !searchState[attribute] && searchContainer) {
        searchContainer.style.display = 'none';
      } else if ((items.length > 15 || searchState[attribute]) && searchContainer) {
        searchContainer.style.display = '';
      }

      if (!showMoreButton && items.length > 5) {
        const bodyElem = containerElem.querySelector('.filter-checkboxes');

        if (bodyElem) {
          const buttonHTML = `
            <button type="button" class="button button-unstyled button--text filter-checkboxes__toggle-btn js-filter-checkboxes__toggle-btn">
              <span class="button__text js-filter-checkboxes__toggle-text">${isShowingMore ? window.searchTranslations?.filters?.showLess || 'Weniger anzeigen' : window.searchTranslations?.filters?.showAll || 'Alle anzeigen'}</span>
            </button>
          `;
          bodyElem.insertAdjacentHTML('beforeend', buttonHTML);

          const newShowMoreButton = bodyElem.querySelector('.js-filter-checkboxes__toggle-btn');
          if (newShowMoreButton) {
            newShowMoreButton.addEventListener('click', (e) => {
              e.stopPropagation();
              toggleShowMore();
            });
          }
        }
      }
    };

    const customRefinementList = connectRefinementList(renderRefinementList);

    return customRefinementList({
      container,
      attribute,
      limit: 16,
      showMore: true,
      showMoreLimit: 50,
      searchable: true,
      searchablePlaceholder: window.searchTranslations?.filters?.searchPlaceholder || 'Suchen...',
    });
  }

  // --- createGroupedNumericRefinementList ---
  static createGroupedNumericRefinementList({ attribute, container, label, breakpoints }) {
    const { connectRefinementList } = window.instantsearch.connectors;
    const hasCustomBreakpoints = Array.isArray(breakpoints) && breakpoints.length >= 2;

    const generateHundredsRanges = (items) => {
      const numericValues = items
        .map((item) => parseThousandsFacetValue(item.value))
        .filter((val) => val !== null);

      if (numericValues.length === 0) return [];

      const minValue = Math.min(...numericValues);
      const maxValue = Math.max(...numericValues);

      const startRange = Math.floor(minValue / 100) * 100;
      const endRange = Math.ceil(maxValue / 100) * 100;

      const ranges = [];
      for (let i = startRange; i <= endRange; i += 100) {
        ranges.push({
          label: `${i}-${i + 99}`,
          min: i,
          max: i + 99,
          maxInclusive: true,
        });
      }

      return ranges;
    };

    const generateBreakpointRanges = (items) => {
      const numericValues = items
        .map((item) => parseDecimalFacetValue(item.value))
        .filter((val) => val !== null);

      if (numericValues.length === 0) return [];

      const maxValue = Math.max(...numericValues);
      const points = extendBreakpointsToCover(breakpoints, maxValue);
      const ranges = [];

      for (let i = 0; i < points.length - 1; i += 1) {
        ranges.push({
          label: formatBreakpointRangeLabel(points[i], points[i + 1]),
          min: points[i],
          max: points[i + 1],
          maxInclusive: false,
        });
      }

      return ranges;
    };

    const findRange = (value, rangeList) => {
      const parseValue = hasCustomBreakpoints ? parseDecimalFacetValue : parseThousandsFacetValue;
      const numValue = parseValue(value);
      if (numValue === null) return null;

      return rangeList.find((range) => {
        if (range.maxInclusive) {
          return numValue >= range.min && numValue <= range.max;
        }

        return numValue >= range.min && numValue < range.max;
      });
    };

    const groupItemsByRange = (items) => {
      const ranges = hasCustomBreakpoints
        ? generateBreakpointRanges(items)
        : generateHundredsRanges(items);
      const groups = new Map();

      ranges.forEach((range) => {
        groups.set(range.label, {
          label: range.label,
          range,
          items: [],
          count: 0,
          isRefined: false,
          value: range.label,
        });
      });

      items.forEach((item) => {
        const range = findRange(item.value, ranges);

        if (range) {
          const group = groups.get(range.label);
          group.items.push(item);
          group.count += item.count;

          if (item.isRefined) {
            group.isRefined = true;
          }
        }
      });

      return Array.from(groups.values()).filter((group) => group.count > 0);
    };

    const renderGroupedRefinementList = (renderOptions, isFirstRender) => {
      const { items, refine, isShowingMore, toggleShowMore } = renderOptions;

      const containerElem =
        typeof container === 'string' ? document.querySelector(container) : container;

      if (!containerElem) return;

      const groupedItems = groupItemsByRange(items);

      if (isFirstRender) {
        containerElem.innerHTML = `
          <div class="filter-dropdown-item">
            <div class="filter-dropdown-item__header">
              <span class="highlighted-text filter-dropdown-item__title h5" role="button">
                <span class="text text--sm text--regular text--line-height-lg text--headline-font text--none-decoration">
                  ${label}
                </span>
              </span>
              <svg width="16" height="16" aria-hidden="false" aria-label="Dropdown arrow" class="icon filter-dropdown-item__icon" style="--icon-fill: none; --icon-stroke: var(--color-foreground);">
                <use xlink:href="#icon-arrow-filter"></use>
              </svg>
            </div>
            <div class="filter-checkboxes filter-dropdown-item__body">
              <div class="filter-checkboxes__list"></div>
              ${
                groupedItems.length > 5
                  ? `
                <button type="button" class="button button-unstyled button--text filter-checkboxes__toggle-btn js-filter-checkboxes__toggle-btn">
                  <span class="button__text js-filter-checkboxes__toggle-text">${window.searchTranslations?.filters?.showAll || 'Alle anzeigen'}</span>
                </button>
              `
                  : ''
              }
            </div>
          </div>
        `;

        const header = containerElem.querySelector('.filter-dropdown-item__header');
        const body = containerElem.querySelector('.filter-dropdown-item__body');
        const dropdownItem = containerElem.querySelector('.filter-dropdown-item');
        const icon = containerElem.querySelector('.filter-dropdown-item__icon');

        if (header && body && dropdownItem) {
          body.style.display = 'none';
          dropdownItem.classList.add('filter-dropdown-item--closed');

          if (icon) {
            icon.style.transform = 'rotate(-180deg)';
          }

          header.addEventListener('click', () => {
            const isOpen = body.style.display !== 'none';

            if (isOpen) {
              body.style.display = 'none';
              dropdownItem.classList.add('filter-dropdown-item--closed');
              dropdownItem.classList.remove('filter-dropdown-item--open');

              if (icon) {
                icon.style.transform = 'rotate(-180deg)';
                icon.style.transition = 'transform 0.3s ease';
              }
            } else {
              body.style.display = '';
              dropdownItem.classList.remove('filter-dropdown-item--closed');
              dropdownItem.classList.add('filter-dropdown-item--open');

              if (icon) {
                icon.style.transform = 'rotate(0deg)';
                icon.style.transition = 'transform 0.3s ease';
              }
            }
          });

          header.style.cursor = 'pointer';
        }
      }

      const listContainer = containerElem.querySelector('.filter-checkboxes__list');
      const showMoreButton = containerElem.querySelector('.js-filter-checkboxes__toggle-btn');
      const dropdownItem = containerElem.querySelector('.filter-dropdown-item');

      if (listContainer) {
        const limit = isShowingMore ? 50 : 5;
        const visibleGroups = groupedItems.slice(0, limit);

        dropdownItem.classList.toggle('filter-dropdown-item--empty', visibleGroups.length === 0);

        listContainer.innerHTML =
          visibleGroups.length > 0
            ? visibleGroups
                .map(
                  (group) => `
            <label class="field field--row filter-checkboxes__input">
              <input class="input ${group.isRefined ? 'input--checked' : ''}" type="checkbox" value="${group.label}" ${group.isRefined ? 'checked' : ''} data-group-range="true" />
              <span class="filter-field__label">${group.label}</span>
              <span class="filter-checkboxes__count-option">${group.count}</span>
            </label>
          `
                )
                .join('')
            : '';

        listContainer.querySelectorAll('input[type="checkbox"]').forEach((checkbox, index) => {
          checkbox.addEventListener('change', () => {
            const group = visibleGroups[index];

            group.items.forEach((item) => {
              if (checkbox.checked && !item.isRefined) {
                refine(item.value);
              } else if (!checkbox.checked && item.isRefined) {
                refine(item.value);
              }
            });
          });
        });
      }

      if (showMoreButton) {
        const showMoreText = showMoreButton.querySelector('.js-filter-checkboxes__toggle-text');

        if (showMoreText) {
          showMoreText.textContent = isShowingMore
            ? window.searchTranslations?.filters?.showLess || 'Weniger anzeigen'
            : window.searchTranslations?.filters?.showAll || 'Alle anzeigen';
        }

        showMoreButton.style.display = groupedItems.length > 5 ? '' : 'none';

        const newButton = showMoreButton.cloneNode(true);
        showMoreButton.parentNode.replaceChild(newButton, showMoreButton);
        newButton.addEventListener('click', (e) => {
          e.stopPropagation();
          toggleShowMore();
        });
      }

      if (!showMoreButton && groupedItems.length > 5) {
        const bodyElem = containerElem.querySelector('.filter-checkboxes');

        if (bodyElem) {
          const buttonHTML = `
            <button type="button" class="button button-unstyled button--text filter-checkboxes__toggle-btn js-filter-checkboxes__toggle-btn">
              <span class="button__text js-filter-checkboxes__toggle-text">${isShowingMore ? window.searchTranslations?.filters?.showLess || 'Weniger anzeigen' : window.searchTranslations?.filters?.showAll || 'Alle anzeigen'}</span>
            </button>
          `;
          bodyElem.insertAdjacentHTML('beforeend', buttonHTML);

          const newShowMoreButton = bodyElem.querySelector('.js-filter-checkboxes__toggle-btn');
          if (newShowMoreButton) {
            newShowMoreButton.addEventListener('click', (e) => {
              e.stopPropagation();
              toggleShowMore();
            });
          }
        }
      }
    };

    const customRefinementList = connectRefinementList(renderGroupedRefinementList);

    return customRefinementList({
      container,
      attribute,
      limit: 1000,
      showMore: true,
      showMoreLimit: 2000,
      searchable: false,
    });
  }

  // --- createExpressTagRefinement ---
  static createExpressTagRefinement({ attribute, container, label }) {
    const { connectRefinementList } = window.instantsearch.connectors;

    const renderExpressTag = (renderOptions, isFirstRender) => {
      const { items, refine } = renderOptions;

      const containerElem =
        typeof container === 'string' ? document.querySelector(container) : container;

      if (!containerElem) return;

      const filteredItems = items
        .filter((item) => item.value === '1')
        .map((item) => ({
          ...item,
          label: window.searchTranslations?.filters?.yes || 'Ja',
        }));

      if (isFirstRender) {
        containerElem.innerHTML = `
          <div class="filter-dropdown-item">
            <div class="filter-dropdown-item__header">
              <span class="highlighted-text filter-dropdown-item__title h5" role="button">
                <span class="text text--sm text--regular text--line-height-lg text--headline-font text--none-decoration">
                  ${label}
                </span>
              </span>
              <svg width="16" height="16" aria-hidden="false" aria-label="Dropdown arrow" class="icon filter-dropdown-item__icon" style="--icon-fill: none; --icon-stroke: var(--color-foreground);">
                <use xlink:href="#icon-arrow-filter"></use>
              </svg>
            </div>
            <div class="filter-checkboxes filter-dropdown-item__body">
              <div class="filter-checkboxes__list"></div>
            </div>
          </div>
        `;

        const header = containerElem.querySelector('.filter-dropdown-item__header');
        const body = containerElem.querySelector('.filter-dropdown-item__body');
        const dropdownItem = containerElem.querySelector('.filter-dropdown-item');
        const icon = containerElem.querySelector('.filter-dropdown-item__icon');

        if (header && body && dropdownItem) {
          body.style.display = 'none';
          dropdownItem.classList.add('filter-dropdown-item--closed');

          if (icon) {
            icon.style.transform = 'rotate(-180deg)';
          }

          header.addEventListener('click', () => {
            const isOpen = body.style.display !== 'none';

            if (isOpen) {
              body.style.display = 'none';
              dropdownItem.classList.add('filter-dropdown-item--closed');
              dropdownItem.classList.remove('filter-dropdown-item--open');

              if (icon) {
                icon.style.transform = 'rotate(-180deg)';
                icon.style.transition = 'transform 0.3s ease';
              }
            } else {
              body.style.display = '';
              dropdownItem.classList.remove('filter-dropdown-item--closed');
              dropdownItem.classList.add('filter-dropdown-item--open');

              if (icon) {
                icon.style.transform = 'rotate(0deg)';
                icon.style.transition = 'transform 0.3s ease';
              }
            }
          });

          header.style.cursor = 'pointer';
        }
      }

      const listContainer = containerElem.querySelector('.filter-checkboxes__list');

      if (listContainer) {
        if (filteredItems.length === 0) {
          containerElem.style.display = 'none';
          return;
        }

        containerElem.style.display = '';

        listContainer.innerHTML = filteredItems
          .map(
            (item) => `
          <label class="field field--row filter-checkboxes__input">
            <input class="input ${item.isRefined ? 'input--checked' : ''}" type="checkbox" value="${item.value}" ${item.isRefined ? 'checked' : ''} />
            <span class="filter-field__label">${item.label}</span>
            <span class="filter-checkboxes__count-option">${item.count}</span>
          </label>
        `
          )
          .join('');

        listContainer.querySelectorAll('input[type="checkbox"]').forEach((checkbox, index) => {
          checkbox.addEventListener('change', () => {
            refine(filteredItems[index].value);
          });
        });
      }
    };

    const customRefinementList = connectRefinementList(renderExpressTag);

    return customRefinementList({
      container,
      attribute,
      limit: 10,
    });
  }

  // Helper function to clean category values
  static cleanCategoryValue(value) {
    return value.replace(/^\["/, '').replace(/"\]$/, '');
  }

  static buildHierarchy(items) {
    const hierarchy = {};

    // First pass: Create all nodes
    items.forEach((item) => {
      const path = AlgoliaUiComponents.cleanCategoryValue(item.value);
      const segments = path.split('/');
      let currentLevel = hierarchy;
      let currentPath = '';

      segments.forEach((segment, index) => {
        currentPath = currentPath ? `${currentPath}/${segment}` : segment;

        if (!currentLevel[segment]) {
          currentLevel[segment] = {
            path: currentPath,
            count: 0,
            children: {},
            isRefined: false,
            level: index,
            value: item.value,
          };
        }

        if (index === segments.length - 1) {
          currentLevel[segment].count = item.count;
          currentLevel[segment].isRefined = item.isRefined;
        }

        currentLevel = currentLevel[segment].children;
      });
    });

    // Second pass: Calculate parent counts
    function calculateCounts(node) {
      let totalCount = node.count || 0;
      const childrenKeys = Object.keys(node.children);

      childrenKeys.forEach((key) => {
        totalCount += calculateCounts(node.children[key]);
      });

      Object.assign(node, {
        count: totalCount,
        hasChildren: childrenKeys.length > 0,
      });

      return totalCount;
    }

    Object.values(hierarchy).forEach((node) => calculateCounts(node));

    return hierarchy;
  }

  static getAllChildPaths(items, parentPath) {
    return items.filter((item) => {
      const itemPath = AlgoliaUiComponents.cleanCategoryValue(item.value);
      return itemPath === parentPath || itemPath.startsWith(`${parentPath}/`);
    });
  }

  static createExpandedStateManager() {
    const expandedPaths = new Set();
    const expandedLevels = new Map();
    const directlySelectedPaths = new Set();

    return {
      isExpanded(path) {
        return expandedPaths.has(path);
      },

      isDirectlySelected(path) {
        return directlySelectedPaths.has(path);
      },

      allPaths() {
        return Array.from(expandedPaths);
      },

      allLevels() {
        return Array.from(expandedLevels.keys());
      },

      allDirectlySelectedPaths() {
        return Array.from(directlySelectedPaths);
      },

      expandPath(path, level) {
        if (expandedLevels.has(level)) {
          const previousPath = expandedLevels.get(level);
          if (previousPath !== path) {
            expandedPaths.delete(previousPath);
          }
        }

        expandedPaths.add(path);
        expandedLevels.set(level, path);
      },

      collapsePath(path, level) {
        expandedPaths.delete(path);
        directlySelectedPaths.delete(path);

        if (expandedLevels.get(level) === path) {
          expandedLevels.delete(level);
        }
      },

      togglePath(path, level, isDirectSelection = true, isAutoSelect = false) {
        if (expandedPaths.has(path)) {
          expandedPaths.delete(path);
          directlySelectedPaths.delete(path);

          if (expandedLevels.get(level) === path) {
            expandedLevels.delete(level);
          }

          if (isAutoSelect) {
            directlySelectedPaths.add(path);
          }

          return false;
        }

        if (expandedLevels.has(level)) {
          const previousPath = expandedLevels.get(level);
          if (previousPath !== path) {
            expandedPaths.delete(previousPath);
            directlySelectedPaths.delete(previousPath);
          }
        }

        if (!isAutoSelect) {
          expandedPaths.add(path);
          expandedLevels.set(level, path);
        } else {
          expandedPaths.delete(path);
          expandedLevels.delete(level);
        }

        if (isDirectSelection) {
          directlySelectedPaths.add(path);
        }

        return !isAutoSelect;
      },

      clear() {
        expandedPaths.clear();
        expandedLevels.clear();
        directlySelectedPaths.clear();
      },
    };
  }

  static isChildOf(childPath, parentPath) {
    const childSegments = childPath.split('/');
    const parentSegments = parentPath.split('/');

    if (childSegments.length !== parentSegments.length + 1) return false;

    for (let i = 0; i < parentSegments.length; i += 1) {
      if (childSegments[i] !== parentSegments[i]) return false;
    }

    return true;
  }

  static renderHierarchicalMenu(renderOptions, isFirstRender) {
    const { items, refine, widgetParams } = renderOptions;

    const containerElem =
      typeof widgetParams.container === 'string'
        ? document.querySelector(widgetParams.container)
        : widgetParams.container;

    if (!containerElem) return;

    const isSearchPage = window.location.pathname.includes('/search');

    if (!containerElem._expandedState) {
      containerElem._expandedState = AlgoliaUiComponents.createExpandedStateManager();
    }

    const expandedState = containerElem._expandedState;

    if (!containerElem._dispose) {
      containerElem._dispose = () => {};
    }

    const hierarchy = AlgoliaUiComponents.buildHierarchy(items);
    const currentUrl = window.location.href;

    if (containerElem._lastUrl && containerElem._lastUrl !== currentUrl) {
      // reset placeholder
    }

    containerElem._lastUrl = currentUrl;

    if (
      !AlgoliaUiComponents.hasLoggedInitialPaths &&
      items &&
      items.length > 0 &&
      (AlgoliaUiComponents.isHierarchicalMenuFirstElementClicked === false || isSearchPage)
    ) {
      const activePaths = items
        .filter((item) => item.isRefined)
        .map((item) => AlgoliaUiComponents.cleanCategoryValue(item.value));

      if (activePaths.length > 0) {
        const urlParams = new URLSearchParams(window.location.search);
        const hasInitialCategories =
          urlParams.has('categories') || urlParams.has('display-category');
        if (hasInitialCategories) {
          const level1Paths = activePaths.map((path) => path.split('/').slice(0, 2).join('/'));
          const uniqueLevel1Paths = [...new Set(level1Paths)];

          if (activePaths.length === 1) {
            const level1Path = activePaths[0].split('/').slice(0, 2).join('/');
            const level0path = activePaths[0].split('/').slice(0, 1).join('/');
            const levelpath = activePaths[0].split('/').slice(0, 3).join('/');

            expandedState.togglePath(level0path, 0, true);
            expandedState.togglePath(level1Path, 1, true);
            expandedState.togglePath(levelpath, 2, true);
          } else if (uniqueLevel1Paths.length === 1) {
            const level0Path = activePaths[0].split('/')[0];
            const level1Path = uniqueLevel1Paths[0];

            expandedState.togglePath(level0Path, 0, true);
            expandedState.togglePath(level1Path, 1, true);
          } else {
            const firstPath = activePaths[0];
            const level0Path = firstPath.split('/')[0];
            expandedState.togglePath(level0Path, 0, true);
          }
        }

        AlgoliaUiComponents.hasLoggedInitialPaths = true;
      }
    }

    const renderLevel = (node) => {
      const segments = node.path.split('/');
      const currentSegment = segments[segments.length - 1];
      const level = segments.length - 1;
      let indentStyle = '';
      if (level === 1) {
        indentStyle = 'padding-left: var(--padding-md);padding-top: 8px;';
      } else if (level > 1) {
        indentStyle = 'padding-left: calc(var(--padding-md) * 3);padding-top: 8px;';
      }
      const activeClasses = [];
      const isDirectlySelected = expandedState.isDirectlySelected(node.path);

      if (isDirectlySelected || (node.isRefined && expandedState.isExpanded(node.path))) {
        activeClasses.push('filter-checkboxes__item--active');
      }

      const isExpanded = expandedState.isExpanded(node.path);
      if (isExpanded) activeClasses.push('filter-checkboxes__item--expanded');

      const children = Object.values(node.children);
      const hasChildren = children.length > 0;

      const isNodeRefined = items.some((item) => {
        const itemPath = AlgoliaUiComponents.cleanCategoryValue(item.value);
        return (itemPath === node.path || itemPath.startsWith(`${node.path}/`)) && item.isRefined;
      });
      return `
        <div class="field field--row filter-checkboxes__input filter-checkboxes__clickable-item category-filter-field ${activeClasses.join(' ')}" 
             style="${indentStyle} cursor: pointer;" 
             data-path="${node.path}" 
             data-has-children="${hasChildren}"
             data-refined="${isNodeRefined}"
             data-level="${level}">
          <div class="filter-checkboxes__content filter-field__label">
            <span class="text">
              ${currentSegment}
            </span>
          </div>
          <span class="filter-checkboxes__count-option" style="flex-shrink: 0; padding-left: var(--padding-2xs);">${node.count}</span>
        </div>
        ${
          hasChildren
            ? `
          <div class="filter-checkboxes__children" style="display: ${isExpanded ? 'block' : 'none'};">
            ${children.map((child) => renderLevel(child)).join('')}
          </div>
        `
            : ''
        }
      `;
    };

    if (isFirstRender) {
      containerElem.innerHTML = `
        <div class="filter-dropdown-item">
          <div class="filter-dropdown-item__header">
            <span class="highlighted-text filter-dropdown-item__title h5" role="button">
              <span class="text text--sm text--regular text--line-height-lg text--headline-font text--none-decoration">
                ${window.searchTranslations?.filters?.kategorien || 'Kategorien'}
              </span>
            </span>
            <svg width="16" height="16" aria-hidden="false" aria-label="Dropdown arrow" class="icon filter-dropdown-item__icon" style="--icon-fill: none; --icon-stroke: var(--color-foreground);">
              <use xlink:href="#icon-arrow-filter"></use>
            </svg>
          </div>
          <div class="filter-checkboxes filter-dropdown-item__body">
            <div class="filter-checkboxes__list"></div>
          </div>
        </div>
      `;

      const header = containerElem.querySelector('.filter-dropdown-item__header');
      const body = containerElem.querySelector('.filter-dropdown-item__body');
      const dropdownItem = containerElem.querySelector('.filter-dropdown-item');
      const icon = containerElem.querySelector('.filter-dropdown-item__icon');

      if (header && body && dropdownItem) {
        body.style.display = 'none';
        dropdownItem.classList.add('filter-dropdown-item--closed');

        if (icon) {
          icon.style.transform = 'rotate(-180deg)';
        }

        header.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const isOpen = !dropdownItem.classList.contains('filter-dropdown-item--closed');

          if (isOpen) {
            body.style.display = 'none';
            dropdownItem.classList.add('filter-dropdown-item--closed');
            dropdownItem.classList.remove('filter-dropdown-item--open');

            if (icon) {
              icon.style.transform = 'rotate(-180deg)';
              icon.style.transition = 'transform 0.3s ease';
            }
          } else {
            body.style.display = '';
            dropdownItem.classList.remove('filter-dropdown-item--closed');
            dropdownItem.classList.add('filter-dropdown-item--open');

            if (icon) {
              icon.style.transform = 'rotate(0deg)';
              icon.style.transition = 'transform 0.3s ease';
            }
          }
        });

        header.style.cursor = 'pointer';
      }
    }

    const listContainer = containerElem.querySelector('.filter-checkboxes__list');
    if (!listContainer) return;

    listContainer.innerHTML = Object.values(hierarchy)
      .map((node) => renderLevel(node))
      .join('');

    const clickableItems = listContainer.querySelectorAll('.filter-checkboxes__clickable-item');

    clickableItems.forEach((clickableItem) => {
      clickableItem.addEventListener('click', async (e) => {
        e.stopPropagation();

        const path = clickableItem.getAttribute('data-path');
        const hasChildren = clickableItem.getAttribute('data-has-children') === 'true';
        const isRefinedFromAttr = clickableItem.getAttribute('data-refined') === 'true';
        const isRefinedFromItems = items.some(
          (item) => AlgoliaUiComponents.cleanCategoryValue(item.value) === path && item.isRefined
        );
        const isRefined = isRefinedFromAttr || isRefinedFromItems;
        const level = parseInt(clickableItem.getAttribute('data-level'), 10);
        const isDirectlySelected = expandedState.isDirectlySelected(path);

        if (isRefined && isDirectlySelected && level === 0) {
          expandedState.clear();
          const refinedItems = items.filter((item) => item.isRefined);
          refinedItems.forEach((item) => refine(item.value));
          return;
        }

        if (isRefined && isDirectlySelected && level > 0) {
          expandedState.togglePath(path, level, false);

          const parentPath = path.split('/').slice(0, -1).join('/');
          const parentElement = listContainer.querySelector(`[data-path="${parentPath}"]`);

          if (parentElement) {
            const parentDataPath = parentElement.getAttribute('data-path');
            const parentLevel = parseInt(parentElement.getAttribute('data-level'), 10);

            const refinedItems = items.filter((item) => item.isRefined);
            refinedItems.forEach((item) => refine(item.value));

            const itemsToRefine =
              parentLevel === 0
                ? AlgoliaUiComponents.getAllChildPaths(items, parentDataPath)
                : items.filter((item) => {
                    const itemPath = AlgoliaUiComponents.cleanCategoryValue(item.value);
                    return (
                      itemPath === parentDataPath ||
                      AlgoliaUiComponents.isChildOf(itemPath, parentDataPath)
                    );
                  });

            items.forEach((item) => {
              const itemPath = AlgoliaUiComponents.cleanCategoryValue(item.value);
              Object.assign(item, {
                isRefined: itemsToRefine.some(
                  (refinedItem) =>
                    AlgoliaUiComponents.cleanCategoryValue(refinedItem.value) === itemPath
                ),
              });
            });

            itemsToRefine.forEach((item) => refine(item.value));
            return;
          }
        }

        const refinedItems = items.filter((item) => item.isRefined);
        refinedItems.forEach((item) => refine(item.value));

        if (level === 0) {
          expandedState.togglePath(path, level, true);

          if (hasChildren) {
            const childrenContainer = clickableItem.nextElementSibling;
            if (
              childrenContainer &&
              childrenContainer.classList.contains('filter-checkboxes__children')
            ) {
              childrenContainer.style.display = 'block';
            }
          }

          const itemsToRefine = AlgoliaUiComponents.getAllChildPaths(items, path);

          items.forEach((item) => {
            const itemPath = AlgoliaUiComponents.cleanCategoryValue(item.value);
            Object.assign(item, {
              isRefined: itemsToRefine.some(
                (refinedItem) =>
                  AlgoliaUiComponents.cleanCategoryValue(refinedItem.value) === itemPath
              ),
            });
          });

          itemsToRefine.forEach((item) => refine(item.value));
        } else if (level === 1) {
          expandedState.togglePath(path, level, true);

          if (hasChildren) {
            const childrenContainer = clickableItem.nextElementSibling;
            if (
              childrenContainer &&
              childrenContainer.classList.contains('filter-checkboxes__children')
            ) {
              childrenContainer.style.display = 'block';
            }
          }

          const itemsToRefine = items.filter((item) => {
            const itemPath = AlgoliaUiComponents.cleanCategoryValue(item.value);
            return itemPath === path || AlgoliaUiComponents.isChildOf(itemPath, path);
          });

          items.forEach((item) => {
            const itemPath = AlgoliaUiComponents.cleanCategoryValue(item.value);
            Object.assign(item, {
              isRefined: itemsToRefine.some(
                (refinedItem) =>
                  AlgoliaUiComponents.cleanCategoryValue(refinedItem.value) === itemPath
              ),
            });
          });

          itemsToRefine.forEach((item) => refine(item.value));
        } else {
          expandedState.togglePath(path, level, true);

          const parentPath = path.split('/').slice(0, -1).join('/');
          const siblings = listContainer.querySelectorAll(`[data-path^="${parentPath}/"]`);

          siblings.forEach((sibling) => {
            if (sibling.getAttribute('data-path') !== path) {
              sibling.classList.remove('filter-checkboxes__item--active');
            }
          });

          clickableItem.classList.add('filter-checkboxes__item--active');

          const exactMatch = items.find(
            (item) => AlgoliaUiComponents.cleanCategoryValue(item.value) === path
          );
          if (exactMatch) {
            refine(exactMatch.value);
          }
        }
      });
    });

    if (
      clickableItems.length > 0 &&
      AlgoliaUiComponents.isHierarchicalMenuFirstElementClicked === false &&
      !isSearchPage
    ) {
      AlgoliaUiComponents.isHierarchicalMenuFirstElementClicked = true;

      const initialParams = new URLSearchParams(window.location.search);
      if (initialParams.has('categories') || initialParams.has('display-category')) {
        return;
      }

      clickableItems.forEach((clickableItem) => {
        const itemLabel = clickableItem.querySelector('.text').textContent.trim().toLowerCase();
        const currentCollectionTitle = document
          .querySelector('.collection-products[data-item-list]')
          ?.dataset.itemList?.trim()
          .toLowerCase();
        if (itemLabel === currentCollectionTitle) {
          const currentPath = clickableItem.getAttribute('data-path');
          const currentLevel = parseInt(clickableItem.getAttribute('data-level'), 10);
          const parentPath = currentPath.split('/').slice(0, -1).join('/');

          if (currentLevel === 0) {
            expandedState.togglePath(currentPath, 0, true);
          } else if (currentLevel === 1) {
            expandedState.togglePath(currentPath, 1, true, true);
            expandedState.togglePath(parentPath, 0, true);
          } else {
            const parentOfParentPath = parentPath.split('/').slice(0, -1).join('/');
            expandedState.togglePath(parentOfParentPath, 0, true);
            expandedState.togglePath(parentPath, 1, true);
            expandedState.togglePath(currentPath, 2, true, true);
          }

          clickableItems.forEach((itemEl) => {
            const path = itemEl.getAttribute('data-path');
            if (!path || !expandedState.isExpanded(path)) return;

            const childrenContainer = itemEl.nextElementSibling;
            if (
              childrenContainer &&
              childrenContainer.classList.contains('filter-checkboxes__children')
            ) {
              childrenContainer.style.display = 'block';
            }
          });

          clickableItem.classList.add('filter-checkboxes__item--active');
        }
      });
    }
  }

  static createHierarchicalMenuWithHeader({ attribute, container, limit = 10 }) {
    const { connectRefinementList } = window.instantsearch.connectors;

    return connectRefinementList(AlgoliaUiComponents.renderHierarchicalMenu)({
      container,
      attribute,
      limit,
      operator: 'or',
      sortBy: ['name:asc'],
    });
  }

  // --- renderCurrentRefinements ---
  static renderCurrentRefinements(renderOptions) {
    const { items, refine, widgetParams } = renderOptions;
    const { container, showResetButton = true, footerResetSelector } = widgetParams;

    const containerElem =
      typeof container === 'string' ? document.querySelector(container) : container;

    if (!containerElem) return;

    const formatAttributeLabel = (attribute) => {
      const cleanAttr = attribute.replace('meta.custom.weitere_informationen.', '');

      const labelMap = {
        vendor: window.searchTranslations?.filters?.manufacturer || 'Hersteller',
        collections: window.searchTranslations?.filters?.collections || 'Kollektionen',
        price: window.searchTranslations?.filters?.price || 'Preis',
        'meta.custom.express_tag':
          window.searchTranslations?.filters?.expressShipping || 'Express Tag',
      };
      if (
        !labelMap[attribute] &&
        FacetMetadata &&
        FacetMetadata.FACET_METADATA &&
        FacetMetadata.FACET_METADATA[attribute]
      ) {
        return FacetMetadata.getFacetTranslation(
          FacetMetadata.FACET_METADATA[attribute].translationKey
        );
      }
      return labelMap[attribute] || cleanAttr;
    };

    const formatRefinementValue = (value, attribute) => {
      if (attribute === 'meta.custom.express_tag' && value === '1') {
        return window.searchTranslations?.filters?.yes || 'Ja';
      }

      return value;
    };

    // Facet values can contain quotes or angle brackets, unescaped they break the
    // data attribute and the refinement lookup on click no longer matches.
    const escapeHtml = (value) =>
      String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const isGroupedNumericAttribute = (attribute) => Boolean(getGroupedNumericMetadata(attribute));

    const groupRefinementsByRange = (attribute, refinements) => {
      const metadata = getGroupedNumericMetadata(attribute);
      const groups = {};

      refinements.forEach((refinement) => {
        const range = getGroupedNumericRangeLabel(refinement.value, metadata?.breakpoints);

        if (range) {
          if (!groups[range]) {
            groups[range] = { label: range, refinements: [] };
          }
          groups[range].refinements.push(refinement);
        }
      });

      return Object.values(groups);
    };

    let html = '<div class="active-filters">';
    const processedItems = [];

    items.forEach((item) => {
      if (item.attribute === 'meta.custom.display_category') return;

      if (item.attribute === 'price' && item.refinements.length > 0) {
        const minRefinement = item.refinements.find((r) => r.operator === '>=');
        const maxRefinement = item.refinements.find((r) => r.operator === '<=');

        if (minRefinement || maxRefinement) {
          processedItems.push({
            attribute: item.attribute,
            refinements: [
              {
                type: 'numeric',
                value: {
                  min: minRefinement ? minRefinement.value : undefined,
                  max: maxRefinement ? maxRefinement.value : undefined,
                },
                operator: 'range',
                originalRefinements: item.refinements,
              },
            ],
          });
        } else {
          processedItems.push(item);
        }
      } else {
        processedItems.push(item);
      }
    });

    processedItems.forEach((item) => {
      const attributeLabel = escapeHtml(formatAttributeLabel(item.attribute));
      const attributeValue = escapeHtml(item.attribute);

      if (isGroupedNumericAttribute(item.attribute)) {
        const groupedRefinements = groupRefinementsByRange(item.attribute, item.refinements);

        groupedRefinements.forEach((group) => {
          html += `
            <button class="active-filter button button-unstyled" aria-label="Active filter for ${attributeLabel}: ${group.label}" type="button" data-attribute="${attributeValue}" data-range="${group.label}">
              <div class="active-filter__content">
                <span class="text text--xx-sm text--regular text--body-font text--none-decoration active-filter__label">
                  ${attributeLabel}:
                </span>
                <span class="text text--xx-sm text--bold text--body-font text--none-decoration active-filter__value">
                  ${group.label}
                </span>
              </div>
              <svg width="12" height="12" aria-hidden="false" aria-label="close active filter" class="active-filter__close-icon" style="--icon-fill: var(--color-foreground); --icon-stroke: none;">
                <use xlink:href="#icon-x-close"></use>
              </svg>
            </button>
          `;
        });
      } else {
        item.refinements.forEach((refinement) => {
          if (
            item.attribute === 'price' &&
            (refinement.type === 'numeric' || refinement.operator === 'range')
          ) {
            const min = refinement.value.min !== undefined ? refinement.value.min : '';
            const max = refinement.value.max !== undefined ? refinement.value.max : '';

            html += `
              <button class="active-filter" aria-label="Active filter for ${attributeLabel}" type="button" data-attribute="${attributeValue}" data-is-price-range="true">
                <div class="active-filter__content">
                  <span class="text text--xx-sm text--regular text--body-font text--none-decoration active-filter__label">
                    ${attributeLabel}:
                  </span>
                  <span class="text text--xx-sm text--bold text--body-font text--none-decoration active-filter__value">
                    ${min}${window.algoliaConfig?.currency || '€'} - ${max}${window.algoliaConfig?.currency || '€'}
                  </span>
                </div>
                <svg width="12" height="12" aria-hidden="false" aria-label="close active filter" class="active-filter__close-icon" style="--icon-fill: var(--color-foreground); --icon-stroke: none;">
                  <use xlink:href="#icon-x-close"></use>
                </svg>
              </button>
            `;
          } else {
            const displayValue = escapeHtml(
              formatRefinementValue(refinement.label || refinement.value, item.attribute)
            );

            html += `
              <button class="active-filter" aria-label="Active filter for ${attributeLabel}: ${displayValue}" type="button" data-attribute="${attributeValue}" data-refinement-value="${escapeHtml(refinement.value)}">
                <div class="active-filter__content">
                  <span class="text text--xx-sm text--regular text--body-font text--none-decoration active-filter__label">
                    ${attributeLabel}:
                  </span>
                  <span class="text text--xx-sm text--bold text--body-font text--none-decoration active-filter__value">
                    ${displayValue}
                  </span>
                </div>
                <svg width="12" height="12" aria-hidden="false" aria-label="close active filter" class="active-filter__close-icon" style="--icon-fill: var(--color-foreground); --icon-stroke: none;">
                  <use xlink:href="#icon-x-close"></use>
                </svg>
              </button>
            `;
          }
        });
      }
    });

    if (showResetButton && processedItems.length > 0) {
      html += `
        <button class="button button-secondary button--medium js-reset-filters" aria-label="remove all filters" type="button">
          <span class="button__label">${window.searchTranslations?.filters?.resetAll || 'Alle zurücksetzen'}</span>
        </button>
      `;
    }

    html += '</div>';
    containerElem.innerHTML = html;

    containerElem.querySelectorAll('.active-filter').forEach((button) => {
      button.addEventListener('click', () => {
        const attribute = button.getAttribute('data-attribute');
        const range = button.getAttribute('data-range');
        const isPriceRange = button.getAttribute('data-is-price-range') === 'true';
        const refinementValue = button.getAttribute('data-refinement-value');

        const item = items.find((i) => i.attribute === attribute);
        if (!item) return;

        if (range && isGroupedNumericAttribute(attribute)) {
          const groupedRefinements = groupRefinementsByRange(item.attribute, item.refinements);
          const group = groupedRefinements.find((g) => g.label === range);

          if (group) {
            group.refinements.forEach((refinement) => {
              refine(refinement);
            });
          }
        } else if (isPriceRange) {
          item.refinements.forEach((refinement) => {
            refine(refinement);
          });
        } else {
          const refinement = item.refinements.find((r) => String(r.value) === refinementValue);
          if (refinement) {
            refine(refinement);
          }
        }
      });
    });

    const resetButton = containerElem.querySelector('.js-reset-filters');
    if (resetButton) {
      resetButton.addEventListener('click', () => {
        items.forEach((item) => {
          item.refinements.forEach((refinement) => {
            refine(refinement);
          });
        });
      });
    }

    if (footerResetSelector) {
      document.querySelectorAll(footerResetSelector).forEach((footerReset) => {
        const shouldHide = processedItems.length === 0;
        // Hiding the button while it is focused would drop focus onto the inert
        // dialog body, so focus moves to the results button first.
        if (shouldHide && document.activeElement === footerReset) {
          const resultsButton = footerReset.parentElement?.querySelector(
            '.filter-drawer__footer-btn'
          );
          if (resultsButton) resultsButton.focus();
        }
        footerReset.hidden = shouldHide;
        // Assigned instead of addEventListener so every render replaces the
        // handler and it never clears through stale refinements.
        footerReset.onclick = () => {
          items.forEach((item) => {
            item.refinements.forEach((refinement) => {
              refine(refinement);
            });
          });
        };
      });
    }
  }

  static openFirstFiveFacets() {
    const facetsContainer = document.querySelector('#facets');

    if (!facetsContainer) {
      setTimeout(() => AlgoliaUiComponents.openFirstFiveFacets(), 100);
      return;
    }

    const openFacets = () => {
      const facets = facetsContainer.querySelectorAll('.filter-dropdown-item');

      let openedCount = 0;
      facets.forEach((facet) => {
        const body = facet.querySelector('.filter-dropdown-item__body');
        const icon = facet.querySelector('.filter-dropdown-item__icon');

        if (openedCount < 5) {
          // Panels the shopper closed by hand stay closed on re-renders
          if (facet.dataset.userToggled !== 'true' && body.style.display === 'none') {
            facet.style.display = '';
            facet.classList.remove('filter-dropdown-item--closed');
            facet.classList.add('filter-dropdown-item--open');
            body.style.display = '';

            if (icon) {
              icon.style.transform = 'rotate(0deg)';
            }
          }
          openedCount += 1;
        }
      });
    };

    const observer = new MutationObserver(() => {
      const facets = facetsContainer.querySelectorAll('.filter-dropdown-item');

      if (facets.length > 0) {
        observer.disconnect();
        setTimeout(openFacets, 200);
      }
    });

    observer.observe(facetsContainer, {
      childList: true,
      subtree: true,
    });

    const existingFacets = facetsContainer.querySelectorAll('.filter-dropdown-item');
    if (existingFacets.length > 0) {
      observer.disconnect();
      setTimeout(openFacets, 200);
    }
  }

  static alternativePaginationIcons(name) {
    const chevron =
      'M4.7071 5.2929c-.3905-.3905-1.0237-.3905-1.4142 0-.3905.3905-.3905 1.0237 0 1.4142l4 4c.3905.3905 1.0237.3905 1.4142 0l4-4c.3905-.3905.3905-1.0237 0-1.4142-.3905-.3905-1.0237-.3905-1.4142 0L8 8.5858l-3.2929-3.293z';
    const paths = {
      first:
        'M13.7071 11.2929 10.4142 8l3.293-3.2929c.3904-.3905.3904-1.0237 0-1.4142-.3906-.3905-1.0238-.3905-1.4143 0l-4 4c-.3905.3905-.3905 1.0237 0 1.4142l4 4c.3905.3905 1.0237.3905 1.4142 0 .3905-.3905.3905-1.0237 0-1.4142zm-6 0L4.4142 8l3.293-3.2929c.3904-.3905.3904-1.0237 0-1.4142-.3906-.3905-1.0238-.3905-1.4143 0l-4 4c-.3905.3905-.3905 1.0237 0 1.4142l4 4c.3905.3905 1.0237.3905 1.4142 0 .3905-.3905.3905-1.0237 0-1.4142z',
      prev: chevron,
      next: chevron,
      last: 'M2.2929 11.2929 5.5858 8l-3.293-3.2929c-.3904-.3905-.3904-1.0237 0-1.4142.3906-.3905 1.0238-.3905 1.4143 0l4 4c.3905.3905.3905 1.0237 0 1.4142l-4 4c-.3905.3905-1.0237.3905-1.4142 0-.3905-.3905-.3905-1.0237 0-1.4142zm6 0L11.5858 8l-3.293-3.2929c-.3904-.3905-.3904-1.0237 0-1.4142.3906-.3905 1.0238-.3905 1.4143 0l4 4c.3905.3905.3905 1.0237 0 1.4142l-4 4c-.3905.3905-1.0237.3905-1.4142 0-.3905-.3905-.3905-1.0237 0-1.4142z',
    };
    const transforms = {
      prev: 'matrix(0 -1 -1 0 16 16)',
      next: 'rotate(-90 8 8)',
    };
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

    svg.setAttribute('class', 'alt-pagination__icon');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute('fill-rule', 'evenodd');
    path.setAttribute('d', paths[name]);

    if (transforms[name]) path.setAttribute('transform', transforms[name]);

    svg.append(path);
    return svg;
  }

  static paginationWindow(currentRefinement, nbPages) {
    const maxVisiblePages = 5;
    let startPage = Math.max(0, currentRefinement - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(nbPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    return { startPage, endPage };
  }

  static bindPaginationClicks(containerElem, refine) {
    containerElem.querySelectorAll('a[href]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();

        const href = link.getAttribute('href');
        if (!href) return;

        const url = new URL(href, window.location.href);
        const urlParams = new URLSearchParams(url.search);
        let pageNumber = 0;

        if (urlParams.has('page')) {
          pageNumber = parseInt(urlParams.get('page'), 10) - 1;
        }

        refine(pageNumber);

        const resultsContainer = document.querySelector('#hits');
        if (resultsContainer) {
          resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  static renderAlternativePagination(renderOptions, containerElem) {
    const { currentRefinement, nbPages, isFirstPage, isLastPage, refine, createURL } =
      renderOptions;
    const fillLabel = (template, page) => String(template || '').replace(/__PAGE__/g, String(page));
    const labels = {
      nav: containerElem.dataset.altPaginationNav || 'Pagination',
      previous: containerElem.dataset.altPaginationPrevious || 'Previous',
      next: containerElem.dataset.altPaginationNext || 'Next',
      page: containerElem.dataset.altPaginationPage || 'Go to page __PAGE__',
      current: containerElem.dataset.altPaginationCurrent || 'Current page, page __PAGE__',
    };
    const control = ({ href, label, iconName, disabled, current, pageNumber }) => {
      const classes = ['alt-pagination__link'];
      const inactive = disabled || current;
      const el = document.createElement(inactive ? 'span' : 'a');

      if (iconName) classes.push('alt-pagination__link--nav');
      if (current) classes.push('alt-pagination__link--current');
      if (disabled) classes.push('alt-pagination__link--disabled');

      el.className = classes.join(' ');
      el.setAttribute('aria-label', label);

      if (current) el.setAttribute('aria-current', 'page');
      if (disabled) el.setAttribute('aria-disabled', 'true');
      if (!inactive) el.setAttribute('href', href);

      if (iconName) {
        el.append(AlgoliaUiComponents.alternativePaginationIcons(iconName));
      } else {
        el.textContent = String(pageNumber);
      }

      return el;
    };
    const { startPage, endPage } = AlgoliaUiComponents.paginationWindow(currentRefinement, nbPages);
    const parts = [
      control({
        href: isFirstPage ? '' : createURL(0),
        label: fillLabel(labels.page, 1),
        iconName: 'first',
        disabled: isFirstPage,
      }),
      control({
        href: isFirstPage ? '' : createURL(currentRefinement - 1),
        label: labels.previous,
        iconName: 'prev',
        disabled: isFirstPage,
      }),
    ];

    for (let i = startPage; i <= endPage; i += 1) {
      const pageNumber = i + 1;
      const isCurrent = i === currentRefinement;

      parts.push(
        control({
          href: isCurrent ? '' : createURL(i),
          label: fillLabel(isCurrent ? labels.current : labels.page, pageNumber),
          pageNumber,
          current: isCurrent,
        })
      );
    }

    parts.push(
      control({
        href: isLastPage ? '' : createURL(currentRefinement + 1),
        label: labels.next,
        iconName: 'next',
        disabled: isLastPage,
      }),
      control({
        href: isLastPage ? '' : createURL(nbPages - 1),
        label: fillLabel(labels.page, nbPages),
        iconName: 'last',
        disabled: isLastPage,
      })
    );

    const nav = document.createElement('nav');

    nav.className = 'alt-pagination';
    nav.setAttribute('aria-label', labels.nav);
    nav.append(...parts);
    containerElem.replaceChildren(nav);
    AlgoliaUiComponents.bindPaginationClicks(containerElem, refine);
  }

  // --- renderPagination ---
  static renderPagination(renderOptions) {
    const { currentRefinement, nbPages, isFirstPage, isLastPage, refine, createURL, widgetParams } =
      renderOptions;
    const { container } = widgetParams;

    const containerElem =
      typeof container === 'string' ? document.querySelector(container) : container;

    if (!containerElem) return;

    if (nbPages <= 1) {
      containerElem.innerHTML = '';
      return;
    }

    if (containerElem.dataset.alternativePagination === 'true') {
      AlgoliaUiComponents.renderAlternativePagination(renderOptions, containerElem);
      return;
    }

    let html = '<div class="pagination" role="navigation" aria-label="Pagination">';

    if (!isFirstPage) {
      html += `<a href="${createURL(currentRefinement - 1)}" class="button button-secondary button--medium pagination__prev" aria-label="Go to previous page">
      <span class="svg-wrapper">
        <svg aria-hidden="false" class="button__icon" style="stroke: rgb(0, 35, 65); fill: none;">
          <use href="#icon-arrow-pagination-left"></use>
        </svg>
        </span>
      </a>`;
    }

    const { startPage, endPage } = AlgoliaUiComponents.paginationWindow(currentRefinement, nbPages);

    if (startPage > 0) {
      html += `<a href="${createURL(0)}" class="pagination-item${currentRefinement === 0 ? ' pagination-item--active' : ''}" aria-label="${currentRefinement === 0 ? 'Current page, page 1' : 'Go to page 1'}"${currentRefinement === 0 ? ' aria-current="page"' : ''}>
        <span class="text text--xx-sm text--bold text--body-font text--none-decoration pagination-item__number">
          1
        </span>
      </a>`;

      if (startPage > 1) {
        html += `<a role="link" aria-disabled="true" class="pagination-item" aria-current="page" aria-label="Current page, page …">
          <span class="text text--xx-sm text--bold text--body-font text--none-decoration pagination-item__number">
            …
          </span>
        </a>`;
      }
    }

    for (let i = startPage; i <= endPage; i += 1) {
      const pageNumber = i + 1;
      const isActive = i === currentRefinement;

      if (isActive) {
        html += `<a role="link" aria-disabled="true" class="pagination-item pagination-item--active" aria-current="page" aria-label="Current page, page ${pageNumber}">
          <span class="text text--xx-sm text--bold text--body-font text--none-decoration pagination-item__number">
            ${pageNumber}
          </span>
        </a>`;
      } else {
        html += `<a href="${createURL(i)}" class="pagination-item" aria-label="Go to page ${pageNumber}">
          <span class="text text--xx-sm text--bold text--body-font text--none-decoration pagination-item__number">
            ${pageNumber}
          </span>
        </a>`;
      }
    }

    if (endPage < nbPages - 1) {
      if (endPage < nbPages - 2) {
        html += `<a role="link" aria-disabled="true" class="pagination-item" aria-current="page" aria-label="Current page, page …">
          <span class="text text--xx-sm text--bold text--body-font text--none-decoration pagination-item__number">
            …
          </span>
        </a>`;
      }

      html += `<a href="${createURL(nbPages - 1)}" class="pagination-item${currentRefinement === nbPages - 1 ? ' pagination-item--active' : ''}" aria-label="${currentRefinement === nbPages - 1 ? `Current page, page ${nbPages}` : `Go to page ${nbPages}`}"${currentRefinement === nbPages - 1 ? ' aria-current="page"' : ''}>
        <span class="text text--xx-sm text--bold text--body-font text--none-decoration pagination-item__number">
          ${nbPages}
        </span>
      </a>`;
    }

    if (!isLastPage) {
      html += `<a href="${createURL(currentRefinement + 1)}" class="button button-secondary button--medium pagination__next" aria-label="Go to next page">
        <span class="svg-wrapper">
        <svg aria-hidden="false" class="button__icon" style="stroke: rgb(0, 35, 65); fill: none;">
          <use href="#icon-arrow-pagination-right"></use>
        </svg>
        </span>
      </a>`;
    }

    html += '</div>';
    containerElem.innerHTML = html;

    AlgoliaUiComponents.bindPaginationClicks(containerElem, refine);
  }

  // --- renderStats ---
  static renderStats(renderOptions, isFirstRender) {
    const { nbHits, page, hitsPerPage, instantSearchInstance } = renderOptions;
    const searchStatus = instantSearchInstance?.status;

    const filterSelectors = [
      '#facets',
      '#facets-mobile',
      '.filter-drawer',
      '.filter-sorting-button',
      '#current-refinements',
      '#current-refinements-mobile',
      '#sorting',
      '#sorting-mobile',
      '.filter-header',
      '.filter-sorting-btn',
    ];

    const preventInitialHide = ['.filter-header'];

    if (window.innerWidth >= 1024) {
      preventInitialHide.push('#facets');
    }

    filterSelectors.forEach((selector) => {
      const element = document.querySelector(selector);
      if (!element) return;

      if (preventInitialHide.includes(selector) && isFirstRender) {
        return;
      }

      if (nbHits === 0) {
        element.setAttribute('style', 'display: none;');
      } else {
        element.removeAttribute('style');
      }
    });

    const footerButtons = document.querySelectorAll('.filter-drawer__footer-btn .button__label');

    footerButtons.forEach((buttonElement) => {
      const countSpan = buttonElement.querySelector('.js-results-count');

      if (countSpan) {
        countSpan.textContent = nbHits;
      } else {
        const displayText = buttonElement.textContent.split(' ').slice(1).join(' ').trim();
        buttonElement.textContent = `${nbHits} ${displayText}`;
      }
    });

    const searchResultsCount = document.querySelector('.algolia-search-header__results-count');
    if (searchResultsCount) {
      const currentText = searchResultsCount.textContent;
      const match = currentText.match(/\((.*?)\)/);

      if (match) {
        const template = match[1];
        const newText = template.replace(/\d+/, nbHits);
        searchResultsCount.textContent = `( ${newText} )`;
      }
    }

    if (isFirstRender) {
      return;
    }

    if (searchStatus === 'loading' || searchStatus === 'stalled') {
      clearTimeout(AlgoliaUiComponents.settledStatsUpdateTimer);
      AlgoliaUiComponents.settledStatsUpdateTimer = null;
      return;
    }

    AlgoliaUiComponents.scheduleSettledStatsUpdate(nbHits, page, hitsPerPage);
  }

  static settledStatsUpdateTimer = null;
  static hasRevealedSettledStats = false;

  static scheduleSettledStatsUpdate(nbHits, page, hitsPerPage) {
    clearTimeout(AlgoliaUiComponents.settledStatsUpdateTimer);
    const delay = AlgoliaUiComponents.hasRevealedSettledStats ? 100 : 250;
    AlgoliaUiComponents.settledStatsUpdateTimer = setTimeout(() => {
      AlgoliaUiComponents.hasRevealedSettledStats = true;
      AlgoliaUiComponents.applySettledStatsUpdate(nbHits, page, hitsPerPage);
    }, delay);
  }

  static applySettledStatsUpdate(nbHits, page, hitsPerPage) {
    clearTimeout(AlgoliaUiComponents.settledStatsUpdateTimer);
    AlgoliaUiComponents.settledStatsUpdateTimer = null;

    const resultsCounter = document.querySelector('.js-algolia-results-counter');
    if (resultsCounter) {
      if (nbHits === 0) {
        resultsCounter.hidden = true;
      } else {
        const start = page * hitsPerPage + 1;
        const end = Math.min((page + 1) * hitsPerPage, nbHits);
        const startEl = resultsCounter.querySelector('.js-algolia-results-start');
        const endEl = resultsCounter.querySelector('.js-algolia-results-end');
        const totalEl = resultsCounter.querySelector('.js-algolia-results-total');

        if (startEl) startEl.textContent = start;
        if (endEl) endEl.textContent = end;
        if (totalEl) totalEl.textContent = nbHits;
        resultsCounter.hidden = false;
      }
    }

    AlgoliaUiComponents.syncActiveCategoryNavCount(nbHits);

    if (nbHits === 0) return;

    const productsCountEl = document.querySelector('.collection-header-cards__title-count');
    if (!productsCountEl) return;

    const template =
      nbHits === 1
        ? productsCountEl.dataset.countTemplateOne
        : productsCountEl.dataset.countTemplateOther;
    if (!template) return;

    productsCountEl.textContent = template.replace('__COUNT__', String(nbHits));
    productsCountEl.removeAttribute('data-algolia-count-pending');
  }

  static syncActiveCategoryNavCount(nbHits) {
    const activeItem =
      document.querySelector('.algolia-category-nav__item--active') ||
      AlgoliaUiComponents.getCurrentCollectionNavItem();

    if (!activeItem || typeof nbHits !== 'number') return;

    let countEl = activeItem.querySelector('.algolia-category-nav__count');
    if (!countEl) {
      const link = activeItem.querySelector('.algolia-category-nav__link');
      if (!link) return;
      countEl = document.createElement('span');
      countEl.className = 'algolia-category-nav__count';
      link.appendChild(countEl);
    }

    const nextText = `(${nbHits})`;
    if (!countEl.hidden && countEl.textContent === nextText) {
      countEl.removeAttribute('data-algolia-count-pending');
      countEl.dataset.algoliaSynced = 'true';
      return;
    }

    countEl.textContent = nextText;
    countEl.hidden = false;
    countEl.removeAttribute('data-algolia-count-pending');
    countEl.dataset.algoliaSynced = 'true';
  }

  static getCurrentCollectionNavItem() {
    const dataEl = document.querySelector('#collection-products-section-data');
    if (!dataEl) return null;

    try {
      const data = JSON.parse(dataEl.textContent || '{}');
      const collectionId = data.collection_id;
      if (!collectionId) return null;
      return document.querySelector(
        `.algolia-category-nav__item[data-collection-id="${collectionId}"]`
      );
    } catch (error) {
      return null;
    }
  }
}
